import type { JSX } from "preact"
import { useEffect, useRef, useState } from "preact/hooks"

import type { WidgetPayload } from "@workspace/contracts/embed"
import { plainTextOfReply } from "@workspace/contracts/embed/plain-text"
import { CHAT_BUBBLE } from "@workspace/contracts/embed/presets"

import { inkOn, partClass, type Look } from "../appearance"
import { useConversation, type Conversation } from "../chat/use-conversation"
import { Drawer, useMobile } from "../drawer"
import { ArrowRight, MessageCircleX, Sparkles, X } from "../icons"
import type { SurfaceProps } from "../surface"
import { Messenger } from "./chat-messenger"
import {
  type ChatConfig,
  useComposerHint,
  useSpotlights,
  useStickToBottom,
  welcomeOf,
} from "./chat-shared"

/**
 * The chat surface, in its four variants, with the preview's inline styles
 * (`widget-preview.tsx`) carried over number for number - the bubble, the
 * chatbar, the box and the stream look on a customer's page the way the
 * editor promised. What the preview mocks (three fixed messages, a hint
 * that only rotates) is live here: a real composer, the visitor's turns,
 * the agent's reply revealed a paragraph at a time, the dots between.
 * The bubble under the Messenger preset is `chat-messenger.tsx`.
 *
 * Stopping is the visitor's: a `MessageCircleX` control - in the panel's
 * header, or beside the send where there is no header - that puts the
 * agent's "Stop the conversation?" card in the thread (`StopCard`); the
 * card's answer ends the conversation or takes it away. The control is
 * there once a conversation exists (the preview shows it mid-conversation,
 * which is the same rule).
 */

/** One message bubble - the visitor's (`mine`) or the agent's. */
function Bubble({
  text,
  mine,
  onDark,
  look,
  config,
  children,
}: {
  text?: string
  mine: boolean
  onDark: boolean
  look: Look
  config: ChatConfig
  children?: JSX.Element
}) {
  const msgInk = inkOn(onDark)
  const { isGlass, accentFill, accentText, rad } = look
  return (
    <div
      class={partClass(config, mine ? "userMessage" : "botMessage", "uq-rise")}
      style={{
        alignSelf: mine ? "flex-end" : "flex-start",
        maxWidth: mine ? "78%" : "84%",
        padding: "8px 10px",
        borderRadius: rad,
        background: mine
          ? accentFill
          : isGlass
            ? "rgba(10,12,18,.42)"
            : `rgba(${msgInk},${onDark ? 0.1 : 0.06})`,
        color: mine ? accentText : isGlass ? "#fff" : `rgb(${msgInk})`,
        backdropFilter: !mine && isGlass ? "blur(6px)" : undefined,
        fontSize: 12,
        lineHeight: 1.45,
        textShadow: !mine && isGlass ? "0 1px 2px rgba(0,0,0,.5)" : undefined,
        whiteSpace: "pre-wrap",
        overflowWrap: "anywhere",
      }}
    >
      {children ?? (mine || text === undefined ? text : plainTextOfReply(text))}
    </div>
  )
}

/** The agent is typing: three dots in an agent bubble. */
const Dots = (props: { onDark: boolean; look: Look; config: ChatConfig }) => (
  <Bubble mine={false} {...props}>
    <span class="uq-dots" aria-label="Typing">
      <span />
      <span />
      <span />
    </span>
  </Bubble>
)

/**
 * The agent asks before the visitor's stop goes through: the card sits in
 * the thread as an agent bubble, "Keep chatting" outlined in the accent,
 * "Stop conversation" filled. Client-side only - it is never a message.
 */
function StopCard({
  chat,
  onDark,
  look,
  config,
}: {
  chat: Conversation
  onDark: boolean
  look: Look
  config: ChatConfig
}) {
  const stopping = chat.stopping
  if (!stopping) return null
  const { isGlass, accent, accentFill, accentText, rad } = look
  const outline = isGlass ? "#fff" : accent
  const button = {
    padding: "5px 10px",
    borderRadius: rad,
    fontSize: 11.5,
    fontWeight: 500,
    lineHeight: 1.3,
    opacity: stopping.busy ? 0.6 : 1,
  }
  return (
    <Bubble mine={false} onDark={onDark} look={look} config={config}>
      <div
        class="uq-stop"
        style={{ display: "flex", flexDirection: "column", gap: 8 }}
      >
        <span style={{ fontWeight: 600 }}>Stop the conversation?</span>
        <span>Do you want to stop this conversation?</span>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => chat.answerStop(false)}
            disabled={stopping.busy}
            style={{
              ...button,
              border: `1px solid ${outline}`,
              color: outline,
            }}
          >
            Keep chatting
          </button>
          <button
            type="button"
            onClick={() => chat.answerStop(true)}
            disabled={stopping.busy}
            style={{ ...button, background: accentFill, color: accentText }}
          >
            {stopping.busy ? "Stopping…" : "Stop conversation"}
          </button>
        </div>
        {stopping.error && (
          <span class="uq-fade" style={{ opacity: 0.7 }}>
            {stopping.error}
          </span>
        )}
      </div>
    </Bubble>
  )
}

/**
 * The stop control where a variant has no header: a ghost beside the send,
 * the field's ink at rest. Absent until a conversation exists.
 */
function StopGhost({
  config,
  look,
  onDark,
  chat,
  size,
}: {
  config: ChatConfig
  look: Look
  onDark: boolean
  chat: Conversation
  size: number
}) {
  if (!chat.sessionId) return null
  const cInk = inkOn(onDark)
  const idle = !chat.busy && !chat.stopping
  return (
    <button
      type="button"
      class={partClass(config, "stopButton")}
      onClick={chat.askToStop}
      disabled={!idle}
      aria-label="Stop conversation"
      style={{
        width: size,
        height: size,
        borderRadius: look.rad,
        color: look.isGlass ? "#fff" : `rgb(${cInk})`,
        opacity: idle ? 0.55 : 0.3,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <MessageCircleX size={size - 9} />
    </button>
  )
}

/** The pill above the composer - opens the CTA's URL in a new tab. */
function CtaPill({
  config,
  look,
  onClick,
}: {
  config: ChatConfig
  look: Look
  onClick: () => void
}) {
  const cta = config.cta?.href?.trim() ? config.cta : null
  if (!cta) return null
  return (
    <a
      class="uq-cta"
      href={cta.href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "9px 12px",
        borderRadius: look.rad,
        background: look.accentFill,
        color: look.accentText,
        fontSize: 12,
        fontWeight: 500,
      }}
    >
      {cta.label || "Learn more"}
    </a>
  )
}

/** The composer: the hint as placeholder, the arrow as the send button. */
function Composer({
  config,
  look,
  onDark,
  hint,
  chat,
  disabled: offline,
  focus,
  withStop = false,
}: {
  config: ChatConfig
  look: Look
  onDark: boolean
  hint: string
  chat: Conversation
  disabled: boolean
  /** Bumped to put the caret in the field - a rule's `autoStart`. */
  focus: number
  /** The stop control beside the send - for a variant with no header. */
  withStop?: boolean
}) {
  const cInk = inkOn(onDark)
  const { isGlass, accentFill, accentText, rad } = look
  const [value, setValue] = useState("")
  const input = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (focus > 0) input.current?.focus()
  }, [focus])
  const spotlit = useSpotlights(input, config)

  // Nothing sends while the stop card waits for its answer.
  const disabled = offline || Boolean(chat.stopping)
  const submit = (event: Event) => {
    event.preventDefault()
    if (disabled) return
    chat.send(value)
    setValue("")
  }
  const hintColor = isGlass
    ? "rgba(255,255,255,.75)"
    : `rgba(${cInk},${onDark ? 0.6 : 0.45})`

  return (
    <form
      class={partClass(config, "composer")}
      onSubmit={submit}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "7px 8px 7px 11px",
        borderRadius: rad,
        border: isGlass
          ? "1px solid rgba(255,255,255,.35)"
          : `1px solid rgba(${cInk},${onDark ? 0.28 : 0.12})`,
        background: isGlass
          ? "rgba(10,12,18,.3)"
          : `rgba(${cInk},${onDark ? 0.08 : 0.02})`,
        backdropFilter: isGlass || onDark ? "blur(6px)" : undefined,
      }}
    >
      <input
        ref={input}
        type="text"
        value={value}
        onInput={(event) => setValue((event.target as HTMLInputElement).value)}
        // Left out while spotlights type: they own the attribute, and a
        // value here would reset the line on every render.
        placeholder={spotlit ? undefined : hint || "Message…"}
        disabled={disabled}
        maxLength={4000}
        aria-label="Message"
        autoComplete="off"
        style={{
          flex: 1,
          fontSize: 11.5,
          color: isGlass ? "#fff" : `rgb(${cInk})`,
          "--uq-placeholder": hintColor,
        }}
      />
      {withStop && (
        <StopGhost
          config={config}
          look={look}
          onDark={onDark}
          chat={chat}
          size={24}
        />
      )}
      {chat.busy ? (
        <button
          type="button"
          class={partClass(config, "sendButton")}
          onClick={chat.stop}
          aria-label="Stop"
          style={{
            width: 24,
            height: 24,
            borderRadius: rad,
            background: accentFill,
            color: accentText,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              background: "currentColor",
              borderRadius: 1,
            }}
          />
        </button>
      ) : (
        <button
          type="submit"
          class={partClass(config, "sendButton")}
          disabled={disabled}
          aria-label="Send"
          style={{
            width: 24,
            height: 24,
            borderRadius: rad,
            background: accentFill,
            color: accentText,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            opacity: disabled ? 0.5 : 1,
          }}
        >
          <ArrowRight size={13} />
        </button>
      )}
    </form>
  )
}

/** The welcome, the turns so far, the dots, the error line - in order. */
function Turns({
  welcome,
  chat,
  onDark,
  look,
  config,
}: {
  welcome: string
  chat: Conversation
  onDark: boolean
  look: Look
  config: ChatConfig
}) {
  const ink = inkOn(onDark)
  return (
    <>
      <Bubble
        text={welcome}
        mine={false}
        onDark={onDark}
        look={look}
        config={config}
      />
      {chat.turns.map((turn) =>
        turn.role === "user" ? (
          <Bubble
            key={turn.id}
            text={turn.parts[0]}
            mine
            onDark={onDark}
            look={look}
            config={config}
          />
        ) : (
          turn.parts
            .slice(0, turn.shown)
            .map((part, index) => (
              <Bubble
                key={`${turn.id}:${index}`}
                text={part}
                mine={false}
                onDark={onDark}
                look={look}
                config={config}
              />
            ))
        )
      )}
      <StopCard chat={chat} onDark={onDark} look={look} config={config} />
      {chat.busy && <Dots onDark={onDark} look={look} config={config} />}
      {chat.error && (
        <div
          class="uq-error uq-fade"
          style={{
            alignSelf: "flex-start",
            display: "flex",
            gap: 8,
            fontSize: 11.5,
            color: look.isGlass ? "rgba(255,255,255,.8)" : `rgba(${ink},.6)`,
            textShadow: look.isGlass ? "0 1px 2px rgba(0,0,0,.5)" : undefined,
          }}
        >
          <span>{chat.error}</span>
          <button
            type="button"
            onClick={chat.retry}
            style={{
              color: look.isGlass ? "#fff" : look.accent,
              fontWeight: 500,
              textDecoration: "underline",
            }}
          >
            Retry
          </button>
        </div>
      )}
    </>
  )
}

/**
 * The card: header, the message column, the CTA and the composer. The card
 * is a FIXED box (`CHAT_BUBBLE.panelHeight`, or the `height` given): the
 * thread scrolls inside it and the composer stays put, so the card is the
 * same size before the first message and after the fiftieth. `maxHeight`
 * is the room the viewport actually has - the box shrinks on a short
 * screen rather than running under the launcher. `fill` is the phone's
 * sheet (`drawer.tsx`): no card edge, the column takes whatever height the
 * sheet has, and the header carries the close the launcher (under the
 * sheet) can no longer offer.
 */
function Panel({
  payload,
  config,
  look,
  chat,
  shadow,
  hint,
  focus,
  onCta,
  fill = false,
  height = CHAT_BUBBLE.panelHeight,
  maxHeight,
  onClose,
}: {
  payload: WidgetPayload
  config: ChatConfig
  look: Look
  chat: Conversation
  shadow: boolean
  hint: string
  focus: number
  onCta: () => void
  fill?: boolean
  height?: number
  maxHeight?: string
  onClose?: () => void
}) {
  const { ink, surfaceBg, hostDark, accentFill, accentText, rad } = look
  const name = payload.agent?.name ?? null
  const initial = name ? name.trim().charAt(0).toUpperCase() : "A"
  const welcome = welcomeOf(config)
  const column = useStickToBottom([
    chat.turns,
    chat.busy,
    chat.error,
    chat.stopping,
  ])
  const live = Boolean(payload.agent?.live)

  return (
    <div
      class={partClass(config, "panel")}
      style={{
        width: "100%",
        borderRadius: fill ? 0 : rad,
        overflow: "hidden",
        border: fill ? undefined : `1px solid rgba(${ink},.12)`,
        boxShadow: shadow ? "0 18px 44px rgba(0,0,0,.16)" : undefined,
        background: surfaceBg,
        display: "flex",
        flexDirection: "column",
        ...(fill ? { flex: 1, minHeight: 0 } : { height, maxHeight }),
      }}
    >
      <div
        class={partClass(config, "header")}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 12px",
          background: accentFill,
          color: accentText,
        }}
      >
        <span
          style={{
            width: 20,
            height: 20,
            borderRadius: "50%",
            background: "rgba(255,255,255,.25)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 10,
            fontWeight: 600,
            color: accentText,
          }}
        >
          {initial}
        </span>
        <span style={{ fontSize: 12, fontWeight: 500 }}>
          {name ? name.split("—")[0]!.trim() : "Chat"}
        </span>
        <span style={{ marginLeft: "auto", fontSize: 10, opacity: 0.8 }}>
          {live ? "online" : "offline"}
        </span>
        {chat.sessionId && (
          <button
            type="button"
            class={partClass(config, "stopButton")}
            onClick={chat.askToStop}
            disabled={chat.busy || Boolean(chat.stopping)}
            aria-label="Stop conversation"
            style={{
              width: 28,
              height: 28,
              marginRight: onClose ? 0 : -6,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: accentText,
              opacity: chat.busy || chat.stopping ? 0.5 : 0.9,
              flexShrink: 0,
            }}
          >
            <MessageCircleX size={15} />
          </button>
        )}
        {onClose && (
          <button
            type="button"
            class={partClass(config, "close")}
            onClick={onClose}
            aria-label="Close chat"
            style={{
              width: 28,
              height: 28,
              marginRight: -6,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: accentText,
              flexShrink: 0,
            }}
          >
            <X size={16} />
          </button>
        )}
      </div>
      <div
        ref={column}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 7,
          padding: 12,
          background: surfaceBg,
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overscrollBehavior: "contain",
        }}
      >
        <Turns
          welcome={welcome}
          chat={chat}
          onDark={hostDark}
          look={look}
          config={config}
        />
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          padding: "0 12px 12px",
          background: surfaceBg,
        }}
      >
        <CtaPill config={config} look={look} onClick={onCta} />
        <Composer
          config={config}
          look={look}
          onDark={hostDark}
          hint={hint}
          chat={chat}
          disabled={!live}
          focus={focus}
        />
      </div>
    </div>
  )
}

interface VariantProps {
  payload: WidgetPayload
  config: ChatConfig
  look: Look
  chat: Conversation
  hint: string
  mobile: boolean
  focus: number
  live: boolean
  welcome: string
  onCta: () => void
}

export function ChatSurface({
  payload,
  look,
  api,
  embed,
  page,
  reporter,
  open,
  setOpen,
  pinned,
  focus,
}: SurfaceProps) {
  const config = payload.config as ChatConfig
  const chat = useConversation(api, payload, embed, page)
  const hint = useComposerHint(config)
  const { accentFill, accentText, rad, side, offsetY } = look
  const mobile = useMobile()
  const live = Boolean(payload.agent?.live)
  const welcome = welcomeOf(config)

  const onCta = () =>
    reporter.report("cta.clicked", {
      widgetId: payload.id,
      sessionId: chat.sessionId ?? undefined,
      payload: { href: config.cta?.href },
    })

  // The preset owns the bubble's whole chrome; the other variants ignore it.
  if (look.isMessenger) {
    return (
      <Messenger
        payload={payload}
        config={config}
        look={look}
        chat={chat}
        mobile={mobile}
        focus={focus}
        open={open}
        setOpen={setOpen}
        pinned={pinned}
        onCta={onCta}
      />
    )
  }

  if (config.variant === "bubble") {
    const panelBottom =
      offsetY + CHAT_BUBBLE.launcherSize + CHAT_BUBBLE.panelGap
    // On a phone the open panel is a sheet from the bottom edge and covers
    // the launcher; the sheet's header and its grab bar close it instead.
    if (open && mobile) {
      return (
        <Drawer look={look} pinned={pinned} onClose={() => setOpen(false)}>
          <Panel
            payload={payload}
            config={config}
            look={look}
            chat={chat}
            shadow={false}
            hint={hint}
            focus={focus}
            onCta={onCta}
            fill
            onClose={pinned ? undefined : () => setOpen(false)}
          />
        </Drawer>
      )
    }
    return (
      <>
        {open && (
          <div
            class="uq-fixed uq-rise"
            style={{
              bottom: panelBottom,
              width: CHAT_BUBBLE.panelWidth,
              ...side,
            }}
          >
            <Panel
              payload={payload}
              config={config}
              look={look}
              chat={chat}
              shadow
              hint={hint}
              focus={focus}
              onCta={onCta}
              maxHeight={`calc(100vh - ${panelBottom + 12}px)`}
            />
          </div>
        )}
        <button
          type="button"
          class={partClass(config, "launcher", "uq-fixed")}
          aria-label={open ? "Close chat" : "Open chat"}
          aria-expanded={open}
          onClick={() => {
            if (open && pinned) return
            setOpen(!open)
          }}
          style={{
            bottom: offsetY,
            width: CHAT_BUBBLE.launcherSize,
            height: CHAT_BUBBLE.launcherSize,
            borderRadius: Math.min(CHAT_BUBBLE.launcherSize / 2, rad * 2),
            background: accentFill,
            color: accentText,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 10px 26px rgba(0,0,0,.24)",
            ...side,
          }}
        >
          <Sparkles size={20} />
        </button>
      </>
    )
  }

  if (config.variant === "box") {
    return (
      <div class="uq-inline">
        <Panel
          payload={payload}
          config={config}
          look={look}
          chat={chat}
          shadow={false}
          hint={hint}
          focus={focus}
          onCta={onCta}
        />
      </div>
    )
  }

  const variantProps = {
    payload,
    config,
    look,
    chat,
    hint,
    mobile,
    focus,
    live,
    welcome,
    onCta,
  }
  if (config.variant === "chatbar") return <Chatbar {...variantProps} />
  return <Stream {...variantProps} />
}

/** The chatbar: a strip along the bottom; the turns rise above its row once a visitor writes. */
function Chatbar({
  config,
  look,
  chat,
  hint,
  mobile,
  focus,
  live,
  welcome,
}: VariantProps) {
  const { ink, hostDark, accentFill, accentText, rad } = look
  const column = useStickToBottom([
    chat.turns,
    chat.busy,
    chat.error,
    chat.stopping,
  ])
  const started = chat.turns.length > 0 || chat.stopping !== null
  const disabled = !live || chat.stopping !== null
  // The strip's own field, not `Composer`: the variant `main` typed its
  // spotlights on, and the one a first cut of the typing missed because it
  // draws its input itself (caught in a browser, 2026-09-29).
  const input = useRef<HTMLInputElement | null>(null)
  const spotlit = useSpotlights(input, config)
  return (
    <div
      class={partClass(config, "panel", "uq-fixed")}
      style={{
        left: 0,
        right: 0,
        bottom: 0,
        padding: "10px 12px",
        background: hostDark ? "rgba(24,27,34,.97)" : "rgba(255,255,255,.97)",
        borderTop: `1px solid rgba(${ink},.12)`,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        boxShadow: "0 -10px 30px rgba(0,0,0,.12)",
      }}
    >
      {started && (
        <div
          ref={column}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 7,
            maxHeight: "min(50vh, 320px)",
            overflowY: "auto",
          }}
        >
          <Turns
            welcome={welcome}
            chat={chat}
            onDark={hostDark}
            look={look}
            config={config}
          />
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span
          class={partClass(config, "launcher")}
          style={{
            width: 26,
            height: 26,
            borderRadius: rad,
            background: accentFill,
            color: accentText,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Sparkles size={14} />
        </span>
        <span
          class={partClass(config, "botMessage")}
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 12,
            color: `rgb(${ink})`,
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
          }}
        >
          {welcome}
        </span>
        <form
          class={partClass(config, "composer")}
          onSubmit={(event) => {
            event.preventDefault()
            const field = (
              event.currentTarget as HTMLFormElement
            ).elements.namedItem("message") as HTMLInputElement | null
            if (!field || disabled) return
            chat.send(field.value)
            field.value = ""
          }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexShrink: 0,
            padding: "6px 8px 6px 11px",
            borderRadius: rad,
            border: `1px solid rgba(${ink},.14)`,
            minWidth: mobile ? 0 : 220,
          }}
        >
          <input
            name="message"
            type="text"
            placeholder={spotlit ? undefined : hint}
            disabled={disabled}
            maxLength={4000}
            aria-label="Message"
            autoComplete="off"
            ref={(el) => {
              input.current = el
              if (el && focus > 0) el.focus()
            }}
            style={{
              flex: 1,
              fontSize: 11.5,
              color: `rgb(${ink})`,
              "--uq-placeholder": `rgba(${ink},.45)`,
            }}
          />
          <StopGhost
            config={config}
            look={look}
            onDark={hostDark}
            chat={chat}
            size={22}
          />
          <button
            type={chat.busy ? "button" : "submit"}
            class={partClass(config, "sendButton")}
            onClick={chat.busy ? chat.stop : undefined}
            disabled={disabled}
            aria-label={chat.busy ? "Stop" : "Send"}
            style={{
              width: 22,
              height: 22,
              borderRadius: rad,
              background: accentFill,
              color: accentText,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {chat.busy ? (
              <span
                style={{
                  width: 7,
                  height: 7,
                  background: "currentColor",
                  borderRadius: 1,
                }}
              />
            ) : (
              <ArrowRight size={12} />
            )}
          </button>
        </form>
      </div>
    </div>
  )
}

/** The stream: the column floats straight on the host, no card - its ink follows the page. */
function Stream({
  config,
  look,
  chat,
  hint,
  mobile,
  focus,
  live,
  welcome,
  onCta,
}: VariantProps) {
  const { hostDark, side, offsetY } = look
  // The preview's photo host counts as dark; here the page sample decides.
  const floatDark = hostDark
  const column = useStickToBottom([
    chat.turns,
    chat.busy,
    chat.error,
    chat.stopping,
  ])
  return (
    <div
      class={partClass(config, "panel", "uq-fixed")}
      style={{
        bottom: offsetY,
        width: mobile ? `calc(100% - ${config.appearance.offsetX * 2}px)` : 320,
        display: "flex",
        flexDirection: "column",
        gap: 7,
        ...side,
      }}
    >
      <div
        ref={column}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 7,
          maxHeight: "min(60vh, 420px)",
          overflowY: "auto",
        }}
      >
        <Turns
          welcome={welcome}
          chat={chat}
          onDark={floatDark}
          look={look}
          config={config}
        />
      </div>
      <CtaPill config={config} look={look} onClick={onCta} />
      <Composer
        config={config}
        look={look}
        onDark={floatDark}
        hint={hint}
        chat={chat}
        disabled={!live}
        focus={focus}
        withStop
      />
    </div>
  )
}
